import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GrcTabs } from '@grc/ui-package';

// Shared with app.scss via inline style on the rail container: the drawer animates
// its own panel between these two, and the shell column has to travel with it.
export const NAV_RAIL_WIDTH = 56;
export const NAV_PANEL_WIDTH = 240;

const USAGE_PATH = /^\/tenants\/([^/]+)\/usage\/?$/;
const LAST_TENANT_KEY = 'platform-admin.last-tenant';

interface NavItem {
  id: string;
  text: string;
  icon: string;
  path?: string;
  disabled?: boolean;
}

/**
 * Drawer panel. Rendered by DevExtreme as a template, which is a portal back into
 * this React tree, so router hooks resolve normally and no props need threading in.
 */
export default function AppNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const usageTenantId = USAGE_PATH.exec(pathname)?.[1];
  const [lastTenantId, setLastTenantId] = useState<string | null>(() =>
    window.sessionStorage.getItem(LAST_TENANT_KEY)
  );

  useEffect(() => {
    if (!usageTenantId) return;
    window.sessionStorage.setItem(LAST_TENANT_KEY, usageTenantId);
    setLastTenantId(usageTenantId);
  }, [usageTenantId]);

  // Usage has no tenant-free route, so its destination is the tenant last looked at.
  // Until one has been opened there is nowhere for the item to go.
  const usageTarget = usageTenantId ?? lastTenantId;

  const items = useMemo<NavItem[]>(
    () => [
      { id: 'tenants', text: 'Tenants', icon: 'detailslayout', path: '/tenants' },
      {
        id: 'usage',
        text: 'Usage',
        icon: 'chart',
        path: usageTarget ? `/tenants/${usageTarget}/usage` : undefined,
        disabled: !usageTarget
      }
    ],
    [usageTarget]
  );

  return (
    <nav className="app-nav-panel">
      {/*
        Selection rides on selectedItem, not selectedIndex: GrcTabs forwards
        selectedItem unconditionally, so an undefined one clears whatever
        selectedIndex had chosen.
      */}
      <GrcTabs
        id="app-nav-tabs"
        dataSource={items}
        orientation="vertical"
        iconPosition="start"
        selectedItem={items[usageTenantId ? 1 : 0]}
        onItemClick={(e: any) => {
          const item = e.itemData as NavItem;
          if (item.path && item.path !== pathname) navigate(item.path);
        }}
      />
    </nav>
  );
}
